'use client'

import React, { useState, useEffect, useRef } from 'react'
import SearchInput from '@dustin-web/microsite-components/src/search-input'
import { useComponentVisible, useTranslations } from '@dustin-web/microsite-components/src/hooks'
import InstantSearchMini from './instant-search-mini/instant-search-mini'
import { DigitalData } from '@dustin-web/microsite-components/src/utils/digital-data'
import type { GetAutocompleteE4Query } from '@dustin-web/microsite-data/src/graphql/product/generated/graphql-types'
import debounce from 'lodash/debounce'

type SearchBarProps = {
  isLoading: boolean
  isFocused: boolean
  searchPhrase: string
  setSearchPhrase: React.Dispatch<React.SetStateAction<string>>
  searchResult: GetAutocompleteE4Query | undefined
  setIsFocused: React.Dispatch<React.SetStateAction<boolean>>
  triggerInputFocus?: boolean
}

const SearchBar = ({
  isLoading,
  isFocused,
  searchResult,
  searchPhrase,
  setSearchPhrase,
  setIsFocused,
  triggerInputFocus,
}: SearchBarProps) => {
  const [suggestion, setSuggestion] = useState<string>('')
  const { translate } = useTranslations()

  useEffect(() => {
    const suggestion = searchResult?.autoComplete?.suggestions?.[0]?.title || ''

    if (searchPhrase.length > 1) {
      setSuggestion(suggestion)
      return
    }

    setSuggestion('')
  }, [searchPhrase, searchResult?.autoComplete?.suggestions])

  const submitSearch = (searchPhrase: string) => {
    const trimmedSearchPhrase = encodeURIComponent(searchPhrase.trim() ?? '')

    if (typeof window === 'undefined') {
      return
    }

    const searchPath = `${window.location.origin}/${translate('SearchPath')}/${trimmedSearchPhrase}`

    window.location.assign(searchPath)

    setIsFocused(false)
    setSearchPhrase('')
  }

  function handleSearchInputFocus(focusState: boolean) {
    if (focusState === isFocused) {
      return
    }

    setIsFocused(focusState)

    if (focusState) {
      DigitalData.pushSearchFieldInteraction()
    }
  }

  return (
    <SearchInput
      data-test-id="header-search-input"
      label={translate('Header_SearchPlaceholder')}
      onChange={setSearchPhrase}
      setSearchInputFocus={handleSearchInputFocus}
      triggerInputFocus={triggerInputFocus}
      onSubmit={submitSearch}
      placeholder={translate('Header_SearchPlaceholder')}
      suggestion={suggestion}
      withDeleteBtn={searchPhrase.length > 0}
      isLoading={isLoading}
    />
  )
}

export type AutocompleteHook = (searchPhrase: string) => {
  data: GetAutocompleteE4Query | undefined
  isLoading: boolean
  error: Error | null
}

type SearchNavProps = {
  triggerInputFocus?: boolean
  useAutocomplete: AutocompleteHook
}

const SearchNav = ({ triggerInputFocus, useAutocomplete }: SearchNavProps) => {
  const { componentRef } = useComponentVisible<HTMLDivElement>(false)
  const firstRender = useRef(true)
  const [isFocused, setIsFocused] = useState(false)
  const [alreadyEmittedTrackingEvent, setAlreadyEmittedTrackingEvent] = useState('')

  const [searchPhrase, setSearchPhrase] = useState('')

  const { data: searchResult, isLoading } = useAutocomplete(searchPhrase)

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (!componentRef.current?.contains(e.target as Node)) {
        setIsFocused(false)
      }
    }

    const handleEscapeKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsFocused(false)
      }
    }

    document.addEventListener('click', handleClickOutside, true)
    document.addEventListener('keydown', handleEscapeKey, true)

    return () => {
      document.removeEventListener('click', handleClickOutside, true)
      document.removeEventListener('keydown', handleEscapeKey, true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false
      return
    }

    const emittedEvent = DigitalData.pushSearchDropdownState(
      isFocused ? 'open' : 'close',
      searchPhrase.length > 1 ? 'autocomplete' : null,
      alreadyEmittedTrackingEvent
    )

    setAlreadyEmittedTrackingEvent(emittedEvent ?? '')

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFocused, searchPhrase])

  return (
    <div ref={componentRef} className="c-header-search-nav" onClick={() => setIsFocused(true)}>
      <SearchBar
        searchPhrase={searchPhrase}
        setSearchPhrase={debounce(setSearchPhrase, 300)}
        setIsFocused={setIsFocused}
        searchResult={searchResult}
        isLoading={isLoading && isFocused}
        isFocused={isFocused}
        triggerInputFocus={triggerInputFocus}
      />
      <InstantSearchMini
        searchPhrase={searchPhrase}
        searchResult={searchResult}
        isLoading={isLoading && isFocused}
        isFocused={isFocused}
      />
    </div>
  )
}

export default SearchNav
